import type {
  PlaybookId,
  PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type { PlaybookNodeSampleToolResultSectionPayload } from "@repo/app-types/entities/playbookNodeSampleToolResult";
import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { IconSparkles } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import type { SampleToolResultSectionEntry } from "../../hooks/useSampleToolResults.js";
import { calculateSectionFeedbackAccuracyWithCurrentGrades } from "./feedbackAccuracyHelpers.js";
import { GraderFeedbackSlideoverContainer } from "./graderFeedbackSlideoverContainer.js";
import { SectionForm } from "./sectionForm.js";
import { SectionSuggestionResults } from "./sectionSuggestionResults.js";

export interface RecordingAssessmentSectionSettingsProps {
  section: PlaybookSectionNode;
  /** Live (published) version of this section, if any, for showing field-level diffs. */
  liveSection?: PlaybookSectionNode;
  /**
   * Called on in-form edits. Optional — suggestion mode renders the form
   * read-only and never invokes this, and ghost sections don't need an
   * in-place edit path.
   */
  onUpdateSection?: (section: PlaybookSectionNode) => void;
  feedback: readonly FeedbackWithCurrentGrade[];
  isReadOnly?: boolean;
  /**
   * The agent's saved sample-tool result for this section, if any. When the
   * candidate_draft differs from `section`, renders banner + read-only form +
   * Results pane (Apply/Discard at the top); otherwise an editable form with
   * the Results pane alongside.
   */
  suggestion?: SampleToolResultSectionEntry;
  /** Apply the agent's proposal for this section. */
  onApplySuggestion?: (finalSection: PlaybookSectionNode) => void;
  /** Drop the saved suggestion for this section (used by Apply & Discard). */
  onDiscardSuggestion?: () => void;
  /** Hard-delete this section from the playbook tree. */
  onDelete?: () => void;
  /** Ask the AI assistant to optimize this section. */
  onOptimizeSection?: (section: PlaybookSectionNode) => void;
  /** Ask the AI assistant to run the sample-applicability tool against this section. */
  onTestSection?: (section: PlaybookSectionNode) => void;
  /** Whether an assistant run is in progress (gates Optimize/Test). */
  isOptimizingSectionOrCriterion?: boolean;
  playbookId?: PlaybookId | null;
}

export const RecordingAssessmentSectionSettings: FC<
  RecordingAssessmentSectionSettingsProps
> = ({
  section,
  liveSection,
  onUpdateSection,
  isReadOnly = false,
  feedback,
  suggestion,
  onApplySuggestion,
  onDiscardSuggestion,
  onDelete,
  onOptimizeSection,
  onTestSection,
  isOptimizingSectionOrCriterion = false,
  playbookId,
}) => {
  const [showFeedbackSlideover, setShowFeedbackSlideover] = useState(false);

  const updateSection = (updates: Partial<PlaybookSectionNode>) => {
    onUpdateSection?.({ ...section, ...updates });
  };

  const feedbackAccuracy = calculateSectionFeedbackAccuracyWithCurrentGrades(
    section.id,
    feedback,
  );

  const isSuggestionMode =
    suggestion !== undefined && isSectionInSuggestionMode(section, suggestion);

  // Show the Results pane only when this section has a matching sample-tool
  // result (a proposal or a check-only Test run). `isSuggestionMode` already
  // implies a suggestion exists, so suggestion mode always shows the pane.
  const hasResults = suggestion !== undefined;

  // Read-only mode (e.g. version-history view): keep the historical
  // single-column form, no Results pane.
  if (isReadOnly) {
    return (
      <div className="space-y-4 p-2">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Section</h3>
        </div>
        <SectionForm
          section={section}
          liveSection={liveSection}
          onUpdateSection={updateSection}
          isReadOnly
        />
      </div>
    );
  }

  const displaySection = isSuggestionMode
    ? mergeSuggestionIntoSection(section, suggestion.candidate_draft)
    : section;
  const diffBaseline = isSuggestionMode ? section : liveSection;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isSuggestionMode ? (
        <div className="-mx-2 -mt-2 flex shrink-0 items-center justify-between rounded-t-lg bg-indigo-50 px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-indigo-900">
            <IconSparkles className="size-4 shrink-0 text-indigo-500" />
            <span>Showing changes suggested by your agent.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={ButtonVariant.Tertiary}
              size={Size.MEDIUM}
              onClick={() => onDiscardSuggestion?.()}
            >
              Discard changes
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              size={Size.MEDIUM}
              onClick={() => {
                onApplySuggestion?.(displaySection);
              }}
            >
              Apply changes
            </Button>
          </div>
        </div>
      ) : null}
      <div
        className={twMerge(
          "grid min-h-0 flex-1 grid-cols-1 gap-x-2",
          hasResults && "md:grid-cols-[1fr_1px_1fr]",
        )}
      >
        <div className="flex min-h-0 flex-col p-2">
          <div className="flex min-h-0 flex-1 flex-col space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                Section
              </h3>
              {!isSuggestionMode && feedbackAccuracy ? (
                <Button
                  variant={ButtonVariant.Tertiary}
                  size={Size.MEDIUM}
                  onClick={() => setShowFeedbackSlideover(true)}
                >
                  View feedback
                </Button>
              ) : null}
            </div>
            <SectionForm
              section={displaySection}
              liveSection={diffBaseline}
              onUpdateSection={updateSection}
              isReadOnly={isSuggestionMode}
            />
            {!isSuggestionMode ? (
              <GraderFeedbackSlideoverContainer
                isOpen={showFeedbackSlideover}
                onClose={() => setShowFeedbackSlideover(false)}
                feedbacks={feedback}
                node={section}
              />
            ) : null}
          </div>
          {!isSuggestionMode ? (
            <div className="mt-auto flex items-center justify-between pt-4">
              {onDelete ? (
                <Button
                  variant={ButtonVariant.Warning}
                  size={Size.MEDIUM}
                  onClick={onDelete}
                >
                  Delete
                </Button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                {onOptimizeSection ? (
                  <Button
                    variant={ButtonVariant.Secondary}
                    size={Size.MEDIUM}
                    icon={<IconSparkles className="size-4" />}
                    onClick={() => onOptimizeSection(section)}
                    isDisabled={
                      isOptimizingSectionOrCriterion || !section.name.trim()
                    }
                  >
                    Optimize
                  </Button>
                ) : null}
                {onTestSection ? (
                  <Button
                    variant={ButtonVariant.Primary}
                    size={Size.MEDIUM}
                    onClick={() => onTestSection(section)}
                    isDisabled={
                      isOptimizingSectionOrCriterion || !section.name.trim()
                    }
                  >
                    Test
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        {hasResults ? (
          <>
            <div
              aria-hidden
              className={twMerge(
                "-mb-2 hidden bg-gray-200 md:block",
                !isSuggestionMode && "-mt-2",
              )}
            />
            <div className="flex min-h-0 flex-col gap-4 p-2">
              <h3 className="shrink-0 text-base font-semibold text-foreground">
                Results
              </h3>
              <div className="min-h-0 flex-1 overflow-y-auto p-1">
                <SectionSuggestionResults
                  aggregates={suggestion?.aggregates}
                  suggestion={suggestion}
                  playbookId={playbookId}
                  playbookNodeId={displaySection.id}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

/**
 * For an existing-section edit suggestion, merge populated `candidate_draft`
 * fields into the current section so the read-only form renders the proposed
 * end-state.
 */
function mergeSuggestionIntoSection(
  current: PlaybookSectionNode,
  candidateDraft: PlaybookNodeSampleToolResultSectionPayload["candidate_draft"],
): PlaybookSectionNode {
  return {
    ...current,
    ...(candidateDraft.name !== undefined ? { name: candidateDraft.name } : {}),
    ...(candidateDraft.description !== undefined
      ? { description: candidateDraft.description }
      : {}),
  };
}

/**
 * For a `new_section` ghost proposal, synthesize a full `PlaybookSectionNode`
 * from the saved `candidate_draft`. Callers can pass the result as the
 * `section` prop alongside `suggestion` to render the ghost in the same shell.
 */
export function buildNewSectionFromSuggestion(
  suggestion: SampleToolResultSectionEntry,
): PlaybookSectionNode {
  const { candidate_draft } = suggestion;
  return {
    type: "section",
    id: suggestion.playbook_node_id,
    name: candidate_draft.name ?? "",
    description: candidate_draft.description ?? "",
    nodes: [],
  };
}

/**
 * True when the section should render with the suggestion banner + read-only
 * form + Results pane. A "check-only" run — where the agent re-tested the
 * section without proposing edits — does not count.
 */
export function isSectionInSuggestionMode(
  section: PlaybookSectionNode,
  suggestion: SampleToolResultSectionEntry | undefined,
): boolean {
  if (!suggestion) return false;
  if (suggestion.kind === "new_section") return true;
  return !isCandidateDraftEquivalentToSection(
    section,
    suggestion.candidate_draft,
  );
}

function isCandidateDraftEquivalentToSection(
  section: PlaybookSectionNode,
  candidateDraft: PlaybookNodeSampleToolResultSectionPayload["candidate_draft"],
): boolean {
  if (
    candidateDraft.name !== undefined &&
    candidateDraft.name !== section.name
  ) {
    return false;
  }
  if (
    candidateDraft.description !== undefined &&
    candidateDraft.description !== (section.description ?? "")
  ) {
    return false;
  }
  return true;
}
